import { createRoot } from "react-dom/client";
import { StrictMode } from "react";

import "../styles/app.css";
import { initTheme } from "../lib/theme.ts";

import { LoginForm } from "../auth/LoginForm.tsx";
import { SignUpForm } from "../auth/SignUpForm.tsx";
import { CheckInboxPage } from "../auth/CheckInboxPage.tsx";
import { VerifiedPage } from "../auth/VerifiedPage.tsx";
import { ForgotPasswordPage } from "../auth/ForgotPasswordPage.tsx";
import { ResetPasswordPage } from "../auth/ResetPasswordPage.tsx";
import { AcceptInvitationPage } from "../auth/AcceptInvitationPage.tsx";
import { SsoSignInForm } from "../auth/SsoSignInForm.tsx";
import { readNextParam } from "../lib/nextParam.ts";

// The public HTML entry: one bundle for the auth screens, the page picked by
// pathname. The server serves it for exactly the public auth paths and never
// gates them. Pathname switches and plain anchors, no router library.
// See ADR-0042.

/**
 * Picks the auth screen by pathname (sign-in, sign-up, check-your-inbox,
 * verified, forgot and reset password, accept-invitation, SSO) and wraps it
 * in the centred auth frame. After sign-in the browser goes to a validated
 * `?next=`, otherwise `/`. Mounted at the public bundle's root.
 */
export function PublicPage({ pathname, search }: { pathname: string; search: string }): JSX.Element {
  const params = new URLSearchParams(search);
  const path = pathname.replace(/\/+$/, "") || "/";
  let screen: JSX.Element;
  if (path === "/sign-up") {
    screen = (
      <SignUpForm
        invitationId={params.get("invitation")}
        onSignedUp={(email) => {
          const invitation = params.get("invitation");
          const q = new URLSearchParams({ email });
          if (invitation) q.set("invitation", invitation);
          window.location.assign(`/check-your-inbox?${q.toString()}`);
        }}
      />
    );
  } else if (path === "/check-your-inbox") {
    const invitation = params.get("invitation");
    screen = (
      <CheckInboxPage
        email={params.get("email")}
        callbackURL={
          invitation
            ? `/accept-invitation?id=${encodeURIComponent(invitation)}`
            : "/"
        }
      />
    );
  } else if (path === "/verified") {
    screen = <VerifiedPage error={params.get("error")} />;
  } else if (path === "/forgot-password") {
    screen = <ForgotPasswordPage />;
  } else if (path === "/reset-password") {
    screen = <ResetPasswordPage token={params.get("token")} error={params.get("error")} />;
  } else if (path === "/accept-invitation") {
    screen = (
      <AcceptInvitationPage
        invitationId={params.get("id")}
        onAccepted={() => window.location.assign("/")}
      />
    );
  } else if (path === "/login/sso" || params.has("sso")) {
    screen = <SsoSignInForm initialSlug={params.get("sso") ?? ""} />;
  } else {
    screen = (
      <LoginForm
        initialEmail={params.get("email") ?? ""}
        onLoggedIn={() => {
          const next = readNextParam(search) ?? "/";
          window.location.assign(next);
        }}
      />
    );
  }
  return (
    <div className="auth-wrap">
      <div className="auth">
        <div className="auth-mark">
          <span className="mark" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="wordmark">
            TSV Software Factory
            <small>the simple vue</small>
          </span>
        </div>
        {screen}
        {/* The footer states what the session actually is. */}
        <div className="auth-foot">
          <div>Session cookie · 7 days, refreshed daily</div>
        </div>
      </div>
    </div>
  );
}

initTheme();
const root = document.getElementById("root");
if (root) {
  createRoot(root).render(
    <StrictMode>
      <PublicPage pathname={window.location.pathname} search={window.location.search} />
    </StrictMode>,
  );
}
